import React, { useContext, useRef, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { toAsciiOnly } from "../Constants/utility";
import { KBContext, SearchContext } from "../Context";
import { Label } from "./StyledComponents/StyledComponents";
import bannerSearchIcon from "../images/bannerSearchIcon.svg";
import searchIcon from "../images/searchIcon.svg";
import closeIcon from "../images/closeIcon.svg";
import { GMAIL_SEARCH_PLACEHOLDER, OMNI_SEARCH_PLACEHOLDER, OMNI_PRODUCT_TYPE, OUTLOOK_PRODUCT_TYPE, SEARCH_PLACEHOLDER } from "../Constants/KBContext.constants";
import { isFeatureEnabled, composeAssetUrl } from "../utils";
import { FTR_KB_AI_SEARCH, FTR_KB_FOR_OUTLOOK, PRODUCT_KB_ORIGIN } from "../Constants";
import ButtonLoader from "./Loaders/ButtonLoader";
import { sendGainsightEvent, KB_WEBSITE_GAINSIGHT_EVENTS } from "../gainSightIntegration";
import SearchSuggestionsDropdown from "./SearchSuggestionsDropdown";
import useNavigateWithSearch from "../Context/KBContext/hooks/useNavigateWithSearch";

const Image_Base_Url = process.env.REACT_APP_IMAGE_BASE_URL || "";

/* Props used only for styled-component styling — must not reach the DOM. */
const customProps = new Set([
  "dropdownOpen", "aiSearch", "loaded", "visible",
  "hasClose", "bannerLeft", "pathname", "bannerData",
  "hasBannerImage",
]);
const shouldForwardProp = (prop) => !customProps.has(prop);

function getSearchPlaceholder(pathname, product, availableFeatures) {
  if (pathname === "/") return "Search for anything...";
  if (
    isFeatureEnabled(FTR_KB_FOR_OUTLOOK, availableFeatures) &&
    globalThis.origin === PRODUCT_KB_ORIGIN
  ) {
    const placeholderKey = [OUTLOOK_PRODUCT_TYPE, OMNI_PRODUCT_TYPE].includes(product)
      ? OMNI_SEARCH_PLACEHOLDER
      : GMAIL_SEARCH_PLACEHOLDER;
    return SEARCH_PLACEHOLDER.replace("{{product}}", placeholderKey);
  }
  return "Search";
}

/* Hover/focus: elevation + subtle lift (shared by SearchInput and BannerInput) */
const searchInputElevationShadow = "0 8px 24px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.06)";

/* Search-only block — no banner, just spacing below for breadcrumbs */
const SearchOnlyWrapper = styled.div`
  width: 100%;
  margin-bottom: 40px;
  position: relative;
`;

/* Search input — 1px border #e2e8f0, 16px radius, 48px height, shadow depth on interaction, placeholder #64748b */
const SearchInput = styled.input.withConfig({ shouldForwardProp })`
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  padding: 12px 48px 12px 44px;
  border: 1px solid #e2e8f0;
  border-bottom: ${(props) => (props.dropdownOpen ? "none" : "1px solid #e2e8f0")};
  border-radius: ${(props) => (props.dropdownOpen ? "16px 16px 0 0" : "16px")};
  background-color: #ffffff;
  background-image: url(${(props) => (props.aiSearch ? bannerSearchIcon : searchIcon)});
  background-repeat: no-repeat;
  background-position: 16px center;
  background-size: ${(props) => (props.aiSearch ? "17px 18px" : "20px 20px")};
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #0f172a;
  outline: none;
  position: relative;
  z-index: ${(props) => (props.dropdownOpen ? "10000" : "auto")};
  box-shadow: ${(props) => (props.dropdownOpen ? "none" : "0 2px 8px rgba(0, 0, 0, 0.06)")};
  transition: border-radius 0.15s ease, background-color 0.15s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: #64748b;
  }

  &:hover {
    box-shadow: ${(props) => (props.dropdownOpen ? "none" : searchInputElevationShadow)};
  }

  &:focus,
  &:focus-visible {
    border-color: ${(props) => (props.dropdownOpen ? "#e2e8f0" : "#cbd5e1")};
    box-shadow: ${(props) => (props.dropdownOpen ? "none" : searchInputElevationShadow)};
  }
`;

const SearchCloseBtn = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchInner = styled.div`
  position: relative;
  width: 100%;
`;

/* Flex column for banner title + search; stays inside banner */
const WrapperContent = styled.div.withConfig({ shouldForwardProp })`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: ${(props) => (props.pathname === "/" || props.pathname === "/submit-issue" ? "16px" : "0")};
  width: 100%;
  max-width: ${(props) => (props.pathname === "/" || props.pathname === "/submit-issue" ? "min(720px, 100%)" : "none")};
  min-height: 0;
  box-sizing: border-box;
  padding: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "0" : "0 180px"};
  position: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "relative" : "absolute"};
  top: ${(props) => (props.pathname === "/" || props.pathname === "/submit-issue" ? "auto" : "50%")};
  left: ${(props) => (props.pathname === "/" || props.pathname === "/submit-issue" ? "auto" : "50%")};
  transform: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "none" : "translate(-50%, -50%)"};
  overflow: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "visible" : (props.dropdownOpen ? "visible" : "hidden")};

  @media screen and (max-width: 1100px) {
    padding: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "0" : "0 90px"};
  }

  @media screen and (max-width: 800px) {
    gap: ${(props) => (props.pathname === "/" || props.pathname === "/submit-issue" ? "12px" : "0")};
    padding: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "0" : "0 50px"};
  }

  @media screen and (max-width: 500px) {
    gap: ${(props) => (props.pathname === "/" || props.pathname === "/submit-issue" ? "10px" : "0")};
    padding: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "0" : "0 24px"};
  }

  transition: padding 1s, gap 1s;
`;

const BannerWrapper = styled.div.withConfig({ shouldForwardProp })`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  max-width: 1000px;
  height: 300px;
  margin: 0 auto;
  background-color: ${(props) =>
    props.hasBannerImage ? "transparent" : props.bannerData?.brandColor || ""};
  border-radius: 20px;
  overflow: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "visible" : (props.dropdownOpen ? "visible" : "hidden")};

  @media screen and (max-width: 700px) {
    height: 220px;
    border-radius: 16px;
  }
  @media screen and (max-width: 500px) {
    height: 180px;
    border-radius: 12px;
  }
`;

const bannerShimmer = keyframes`
  0% { background-position: -600px 0; }
  100% { background-position: 600px 0; }
`;

/* Shimmer placeholder shown while the banner image is loading */
const BannerShimmer = styled.div.withConfig({ shouldForwardProp })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, #e8e8e8 25%, #d4d4d4 50%, #e8e8e8 75%);
  background-size: 600px 100%;
  animation: ${bannerShimmer} 1.5s ease-in-out infinite;
  border-radius: inherit;
  z-index: 0;
  transition: opacity 0.3s ease;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  pointer-events: none;
`;

/* Image fills banner container; height follows wrapper for responsiveness */
const BannerImage = styled.img.withConfig({ shouldForwardProp })`
  display: block;
  width: 100%;
  height: 100%;
  min-height: 180px;
  object-fit: cover;
  object-position: center;
  border-radius: inherit;
  opacity: ${(props) => (props.loaded ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

/* Overlay for text + search on top of the image; clip so content stays inside banner */
const BannerOverlay = styled.div.withConfig({ shouldForwardProp })`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 80px;
  box-sizing: border-box;
  overflow: ${(props) =>
    props.pathname === "/" || props.pathname === "/submit-issue" ? "visible" : (props.dropdownOpen ? "visible" : "hidden")};

  @media screen and (max-width: 1100px) {
    padding: 48px 64px;
  }
  @media screen and (max-width: 700px) {
    padding: 32px 24px;
  }
  @media screen and (max-width: 500px) {
    padding: 20px 16px;
  }
`;

/* Banner search input; responsive width, height, font and padding.
   When dropdownOpen, the portal card provides white bg + shadow,
   so this input becomes transparent and sits on top (z-index). */
const BannerInput = styled.input.withConfig({ shouldForwardProp })`
  --banner-input-height: ${(props) => (props.pathname === "/" ? "24px" : "28px")};
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: ${(props) => (props.pathname === "/" ? "600px" : "750px")};
  outline: none !important;
  height: var(--banner-input-height);
  min-height: var(--banner-input-height);
  border: ${(props) =>
    props.dropdownOpen
      ? props.pathname === "/"
        ? "1px solid #E8E8E8"
        : "1px solid transparent"
      : props.pathname === "/"
        ? "1px solid #E8E8E8"
        : "0px"};
  border-bottom: ${(props) =>
    props.dropdownOpen
      ? "none"
      : props.pathname === "/"
        ? "1px solid #E8E8E8"
        : "0px"};
  border-radius: ${(props) =>
    props.dropdownOpen
      ? props.pathname === "/"
        ? "16px 16px 0 0"
        : "calc(var(--banner-input-height) / 2) calc(var(--banner-input-height) / 2) 0 0"
      : props.pathname === "/"
        ? "16px"
        : "calc(var(--banner-input-height) / 2)"};
  font-family: Inter, sans-serif;
  font-size: ${(props) => (props.pathname === "/" ? "16px" : "24px")};
  font-style: normal;
  font-weight: 400;
  line-height: 1.25;
  letter-spacing: 0;
  text-align: left;
  padding: ${(props) =>
    props.pathname === "/" ? "25px 24px 25px 52px" : "0 16px 0 65px"};
  margin: 0;
  background: url(${(props) => (props.aiSearch ? bannerSearchIcon : searchIcon)}) no-repeat;
  background-position: ${(props) =>
    props.pathname === "/" ? "22px center" : "2.5% 18px"};
  background-size: 17px 18px;
  background-color: #FFFFFF;
  box-shadow: ${(props) =>
    props.dropdownOpen
      ? "none"
      : props.pathname === "/"
        ? "0px 2px 8px rgba(0, 0, 0, 0.06)"
        : "none"};
  position: relative;
  z-index: ${(props) => (props.dropdownOpen ? "10000" : "auto")};
  transition: transform 0.2s ease, box-shadow 0.2s ease, height 0.2s, padding 0.2s,
    border-radius 0.15s ease, background-color 0.15s ease;

  &::placeholder {
    font-family: Inter, sans-serif;
    font-size: ${(props) => (props.pathname === "/" ? "16px" : "26px")};
    font-weight: 400;
    line-height: 1.25;
    letter-spacing: 0;
    color: ${(props) => (props.pathname === "/" ? "#64748B" : "#8F97B2")};
    opacity: 1;
  }

  &:hover {
    box-shadow: ${(props) => (props.dropdownOpen ? "none" : searchInputElevationShadow)};
  }

  &:focus,
  &:focus-visible {
    box-shadow: ${(props) => (props.dropdownOpen ? "none" : searchInputElevationShadow)};
  }

  @media screen and (max-width: 700px) {
    max-width: 100%;
    height: 52px;
    min-height: 52px;
    border-radius: ${(props) =>
    props.dropdownOpen
      ? props.pathname === "/"
        ? "16px 16px 0 0"
        : "26px 26px 0 0"
      : props.pathname === "/"
        ? "16px"
        : "26px"};
    padding: ${(props) =>
    props.pathname === "/" ? "18px 18px 18px 44px" : "0 14px 0 48px"};
    background-position: ${(props) =>
    props.pathname === "/" ? "16px center" : "14px center"};
    background-size: ${(props) => (props.aiSearch ? "17px 18px" : "20px 20px")};
    font-size: ${(props) => (props.pathname === "/" ? "15px" : "18px")};

    &::placeholder {
      font-size: ${(props) => (props.pathname === "/" ? "15px" : "18px")};
    }
  }

  @media screen and (max-width: 500px) {
    height: 48px;
    min-height: 48px;
    border-radius: ${(props) =>
    props.dropdownOpen
      ? props.pathname === "/"
        ? "16px 16px 0 0"
        : "12px 12px 0 0"
      : props.pathname === "/"
        ? "16px"
        : "12px"};
    padding: ${(props) =>
    props.pathname === "/" ? "14px 14px 14px 40px" : "0 12px 0 40px"};
    background-position: ${(props) =>
    props.pathname === "/" ? "14px center" : "12px center"};
    background-size: ${(props) => (props.aiSearch ? "15px 16px" : "18px 18px")};
    font-size: 14px;

    &::placeholder {
      font-size: 14px;
    }
  }
`;

export const AbsoluteWrapper = styled.div`
  width: ${(props) => props.width || "100%"};
  position: ${(props) => props.position};
  top: ${(props) => props.top};
  left: ${(props) => props.left};
  right: ${(props) => props.right};
  bottom: ${(props) => props.bottom};
  z-index: ${(props) => props.zIndex};
  transform: ${(props) => props.transform};
  cursor: ${(props) => props.cursor};

  @media screen and (max-width: 1100px) {
    width: ${(props) => props.search === true && "fit-content"};
    width: ${(props) => props.pathname !== "/" && "100%"};
  }

  transition: 1s;
`;

const HoverAbsolute = styled(AbsoluteWrapper)`
  display: flex;
  height: 30px;
  width: 30px !important;
  justify-content: center;
  align-items: center;
  border-radius: 50%;

  &:hover {
    background: rgb(245, 245, 245);
  }
`;

/* Search bar wrapper; full width on small screens */
const SearchBarWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 700px) {
    max-width: 100%;
  }
`;

function ClearSearchButton({ loading, onClear }) {
  return (
    <SearchCloseBtn type="button" onClick={onClear} aria-label="Clear search">
      {loading ? <ButtonLoader h="20px" w="20px" /> : <img style={{ height: 20, width: 20 }} src={closeIcon} alt="" />}
    </SearchCloseBtn>
  );
}

/* Enter-key badge shown inside the search bar (Figma 138:4148) */
const EnterKeyBadgeWrapper = styled.div.withConfig({ shouldForwardProp })`
  position: absolute;
  right: ${(props) => (props.hasClose ? "52px" : "16px")};
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 1px 4px;
  background: #f6f5f4;
  border-radius: 4px;
  z-index: 10001;
  pointer-events: none;
`;

const EnterKeyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.5 3V8.5H4.5M4.5 8.5L7 6M4.5 8.5L7 11"
      stroke="#5B5853"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function BannerSearchInput({
  pathname,
  searchInput,
  searchPlaceholder,
  onInputChange,
  onKeyDown,
  onSearchClick,
  onClear,
  loading,
  Wrapper,
  inputRef,
  dropdownOpen,
  showEnterBadge,
  useAiSearchIcon,
  dropdownNode,
}) {
  const showClear = searchInput.length > 0;

  return (
    <Wrapper position="relative" search={showClear} width={pathname === "/" ? "100%" : "fit-content"}>
      <BannerInput
        ref={inputRef}
        bannerLeft={pathname.slice(1) === ""}
        pathname={pathname}
        dropdownOpen={dropdownOpen}
        aiSearch={useAiSearchIcon}
        type="text"
        placeholder={searchPlaceholder}
        value={searchInput}
        onChange={onInputChange}
        onKeyDown={onKeyDown}
        onClick={onSearchClick}
      />
      {showClear && pathname === "/" && showEnterBadge && !dropdownOpen && (
        <EnterKeyBadgeWrapper hasClose>
          <EnterKeyIcon />
        </EnterKeyBadgeWrapper>
      )}
      {showClear && (
        <HoverAbsolute
          position="absolute"
          width="auto !important"
          cursor="pointer"
          pathname={pathname}
          right="16px"
          top="50%"
          transform="translateY(-50%)"
          style={{ zIndex: 10001 }}
          onClick={onClear}
        >
          {loading ? <ButtonLoader h="20px" w="20px" /> : <img style={{ zIndex: 10, height: 20, width: 20 }} src={closeIcon} alt="" />}
        </HoverAbsolute>
      )}
      {dropdownOpen && dropdownNode}
    </Wrapper>
  );
}

export default function BannerSection({ bannerData }) {
  const { setSearchInput, searchInput, setSearchList, searchList, loading, triggerSearch } =
    useContext(SearchContext);
  const { availableFeatures } = useContext(KBContext);
  const navigate = useNavigate();
  const navigateWithSearch = useNavigateWithSearch();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const product = searchParams.get("product");

  const isHomePage = pathname === "/";
  const isSubmitIssuePage = pathname.slice(1) === "submit-issue";
  const isCategoryPage = !isHomePage && !isSubmitIssuePage;
  const searchPlaceholder = getSearchPlaceholder(pathname, product, availableFeatures);
  const isAISearchEnabled = isFeatureEnabled(FTR_KB_AI_SEARCH, availableFeatures);
  const bannerInputRef = useRef(null);
  const prevSearchInput = useRef(searchInput);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (prevSearchInput.current !== "" && searchInput === "") {
      requestAnimationFrame(() => bannerInputRef.current?.focus());
    }
    prevSearchInput.current = searchInput;
  }, [searchInput]);

  // Retain initial search query in the bar when on /search so user knows what was searched
  const searchQuery = searchParams.get("q") || "";
  useEffect(() => {
    if (pathname === "/search") {
      setSearchInput(searchQuery);
    }
  }, [pathname, searchQuery]);

  const handleBannerInputChange = (e) => {
    setSearchInput(toAsciiOnly(e.target.value));
  };

  const submitSearchWithAI = () => {
    if (searchInput.length === 0) return;
    if (isAISearchEnabled) {
      const params = new URLSearchParams(searchParams);
      params.set("q", searchInput);
      setSearchList(false);
      if (pathname !== "/search") setSearchInput("");
      navigate(`/search?${params.toString()}`, {
        state: { searchKey: Date.now() },
        replace: pathname === "/search",
      });
    } else {
      triggerSearch();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" || searchInput.length === 0) return;
    submitSearchWithAI();
  };

  const handleSearchClickEvent = () => {
    sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_INITIATED);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchList(false);
  };

  /* Show dropdown only when AI search is on (legacy: no dropdown, full-page results only). */
  const showDropdown =
    isAISearchEnabled &&
    pathname !== "/search" &&
    searchInput.length > 0 &&
    Array.isArray(searchList) &&
    (searchList.length > 0 || !loading);

  const handleSuggestionSelect = (item) => {
    sendGainsightEvent(KB_WEBSITE_GAINSIGHT_EVENTS.KB_SEARCH_RESULT_CLICKED, {
      search_query: searchInput,
      clicked_article_slug: item.slug,
    });
    setSearchInput("");
    setSearchList(false);
    const catSlug = item.categorySlug || (item.fullPath || "").split("/").filter(Boolean)[0];
    const path = item.fullPath || (catSlug ? `/${catSlug}/${item.slug}` : `/article/${item.slug}`);
    navigateWithSearch(path);
  };

  const handleViewAllResults = () => {
    if (!isAISearchEnabled) return;
    const params = new URLSearchParams(searchParams);
    params.set("q", searchInput);
    setSearchList(false);
    navigate(`/search?${params.toString()}`, {
      state: { searchKey: Date.now() },
    });
  };

  const handleCloseDropdown = () => {
    setSearchList(false);
  };

  /* Article listing / preview / any non-home: search-only bar, same AI search behavior as home */
  if (isCategoryPage) {
    return (
      <SearchOnlyWrapper>
        <SearchInner style={{ position: "relative" }}>
          <SearchInput
            ref={bannerInputRef}
            dropdownOpen={showDropdown}
            aiSearch={isAISearchEnabled}
            type="text"
            placeholder="Search for anything..."
            value={searchInput}
            onChange={handleBannerInputChange}
            onKeyDown={handleKeyDown}
            onClick={handleSearchClickEvent}
          />
          {searchInput.length > 0 && isAISearchEnabled && !showDropdown && (
            <EnterKeyBadgeWrapper hasClose>
              <EnterKeyIcon />
            </EnterKeyBadgeWrapper>
          )}
          {searchInput.length > 0 && <ClearSearchButton loading={loading} onClear={handleClearSearch} />}
          {showDropdown && (
            <SearchSuggestionsDropdown
              inputRef={bannerInputRef}
              items={searchList}
              onSelect={handleSuggestionSelect}
              onViewAll={handleViewAllResults}
              isAIEnabled={isAISearchEnabled}
              onClose={handleCloseDropdown}
              onPressEnterToSearch={submitSearchWithAI}
            />
          )}
        </SearchInner>
      </SearchOnlyWrapper>
    );
  }

  const bannerImageUrl = composeAssetUrl(bannerData?.bannerImage, Image_Base_Url);
  const hasBannerImage = !!bannerImageUrl;

  const bannerContent = (
    <WrapperContent pathname={pathname} dropdownOpen={showDropdown}>
      {isHomePage && (
        <Label responsive bannerColor={bannerData.bannerTextColor} className="bannerText" data-hero>
          {bannerData.bannerText || "Hi, How can we help you?"}
        </Label>
      )}
      {isSubmitIssuePage && (
        <Label padding="0" responsive bannerColor={bannerData.bannerTextColor} className="bannerText">
          {bannerData.helpPageHeading}
        </Label>
      )}
      {!isSubmitIssuePage && (
        isHomePage ? (
          <SearchBarWrapper>
            <BannerSearchInput
              pathname={pathname}
              searchInput={searchInput}
              searchPlaceholder={searchPlaceholder}
              onInputChange={handleBannerInputChange}
              onKeyDown={handleKeyDown}
              onSearchClick={handleSearchClickEvent}
              onClear={handleClearSearch}
              loading={loading}
              Wrapper={AbsoluteWrapper}
              inputRef={bannerInputRef}
              dropdownOpen={showDropdown}
              showEnterBadge={isAISearchEnabled}
              useAiSearchIcon={isAISearchEnabled}
              dropdownNode={
                <SearchSuggestionsDropdown
                  inputRef={bannerInputRef}
                  items={searchList}
                  onSelect={handleSuggestionSelect}
                  onViewAll={handleViewAllResults}
                  isAIEnabled={isAISearchEnabled}
                  onClose={handleCloseDropdown}
                  onPressEnterToSearch={submitSearchWithAI}
                />
              }
            />
          </SearchBarWrapper>
        ) : (
          <BannerSearchInput
            pathname={pathname}
            searchInput={searchInput}
            searchPlaceholder={searchPlaceholder}
            onInputChange={handleBannerInputChange}
            onKeyDown={handleKeyDown}
            onSearchClick={handleSearchClickEvent}
            onClear={handleClearSearch}
            loading={loading}
            Wrapper={AbsoluteWrapper}
            inputRef={bannerInputRef}
            dropdownOpen={showDropdown}
            showEnterBadge={isAISearchEnabled}
            useAiSearchIcon={isAISearchEnabled}
            dropdownNode={
              <SearchSuggestionsDropdown
                inputRef={bannerInputRef}
                items={searchList}
                onSelect={handleSuggestionSelect}
                onViewAll={handleViewAllResults}
                isAIEnabled={isAISearchEnabled}
                onClose={handleCloseDropdown}
                onPressEnterToSearch={submitSearchWithAI}
              />
            }
          />
        )
      )}
    </WrapperContent>
  );

  return (
    <>
      <BannerWrapper pathname={pathname} bannerData={bannerData} hasBannerImage={hasBannerImage} dropdownOpen={showDropdown}>
        {hasBannerImage && (
          <>
            <BannerShimmer visible={!imageLoaded} />
            <BannerImage
              src={bannerImageUrl}
              alt="Banner"
              loaded={imageLoaded}
              onLoad={() => setImageLoaded(true)}
            />
          </>
        )}
        <BannerOverlay pathname={pathname} dropdownOpen={showDropdown}>
          {bannerContent}
        </BannerOverlay>
      </BannerWrapper>
    </>
  );
}
